"use client";

import { motion } from "framer-motion";
import { Star, Clock, User, ArrowRight, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import Image from "next/image";

export interface CourseType {
  id: string;
  title: string;
  instructor: string;
  rating: number;
  reviews: number;
  price: string;
  level: "Beginner" | "Intermediate" | "Advanced" | "All Levels" | "Beginner to Pro";
  duration: string;
  image: string;
  purchased?: boolean; // set by the courses page when user is enrolled
}

export function CourseCard({ course, index }: { course: CourseType; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="group relative rounded-2xl border border-border bg-card overflow-hidden shadow-sm hover:shadow-[0_0_30px_rgba(108,92,231,0.15)] transition-all duration-300"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="relative h-48 w-full overflow-hidden bg-muted">
        <Image
          src={course.image}
          alt={course.title}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-110"
        />
        {/* Level badge */}
        <div className="absolute top-4 left-4 z-20">
          <Badge
            variant="secondary"
            className="bg-black/50 backdrop-blur-md border-white/10 text-white hover:bg-black/70"
          >
            {course.level}
          </Badge>
        </div>

        {/* Purchased badge — top right */}
        {course.purchased && (
          <div className="absolute top-4 right-4 z-20">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-green-500/90 text-white backdrop-blur-md shadow-lg">
              <CheckCircle2 size={12} />
              Purchased!
            </span>
          </div>
        )}
      </div>

      <div className="p-6 relative z-20 bg-card">
        <div className="flex items-center justify-between mb-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Clock size={14} className="text-mont-violet" />
            <span>{course.duration}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <User size={14} className="text-mont-orange" />
            <span>{course.instructor}</span>
          </div>
        </div>

        <Link href={`/courses/${course.id}`}>
          <h3 className="font-bold font-heading text-lg mb-2 text-foreground line-clamp-2 hover:text-mont-violet transition-colors">
            {course.title}
          </h3>
        </Link>

        <div className="flex items-center gap-2 mb-6">
          <div className="flex items-center text-yellow-400">
            <Star size={16} fill="currentColor" />
            <span className="ml-1 text-sm font-medium text-foreground">{course.rating}</span>
          </div>
          <span className="text-xs text-muted-foreground">({course.reviews} reviews)</span>
        </div>

        <div className="flex items-center justify-between mt-auto">
          {course.purchased ? (
            <span className="text-sm font-semibold text-green-400 flex items-center gap-1.5">
              <CheckCircle2 size={15} /> Enrolled
            </span>
          ) : (
            <span className="font-bold text-xl text-foreground">{course.price}</span>
          )}
          <Link href={`/courses/${course.id}`}>
            <Button
              variant="ghost"
              size="sm"
              className={`group/btn hover:bg-mont-violet/10 ${course.purchased ? "text-green-400 hover:text-green-400" : "text-mont-violet hover:text-mont-violet"}`}
            >
              {course.purchased ? "Go to Course" : "View Course"}
              <ArrowRight size={16} className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
